import { useMemo, useState } from "react";
import { Container, Section, Heading, Lede, Eyebrow, LinkButton } from "../components/ui";
import { Seo } from "../lib/head";
import { FaqSection, faqPageJsonLd, type Faq } from "../components/marketing";

const FAQS: Faq[] = [
  {
    q: "Where does the ~50% time saving come from?",
    a: "Charts are ingested automatically (no manual entry), a deterministic rules engine plus AI surfaces the flags and drafts the rationale, and e-signature and invoicing happen in the same workflow. Pharmacists review and finalize instead of building each review from scratch. Actual results vary by facility mix and chart complexity.",
  },
  {
    q: "Is this an estimate or a guarantee?",
    a: "It's an estimate to size the opportunity, based on the reduction rate you choose. Run a free first-facility review to see the real number on your own caseload.",
  },
  {
    q: "Does the calculator send my numbers anywhere?",
    a: "No. The math runs entirely in your browser — nothing is submitted or stored.",
  },
];

const fmtInt = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const fmtUsd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

function Field({
  label,
  suffix,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  suffix?: string;
  value: number;
  min: number;
  max: number;
  onChange: (n: number) => void;
}) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-ink-900">{label}</span>
      <div className="mt-2 flex items-center gap-2 rounded-xl border border-border bg-surface px-3 focus-within:border-forest-700">
        <input
          type="number"
          inputMode="numeric"
          min={min}
          max={max}
          value={Number.isFinite(value) ? value : ""}
          onChange={(e) => onChange(Math.max(min, Math.min(max, Number(e.target.value) || 0)))}
          className="w-full bg-transparent py-2.5 text-ink-900 outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
        />
        {suffix ? <span className="whitespace-nowrap text-sm text-ink-500">{suffix}</span> : null}
      </div>
    </label>
  );
}

export function RoiCalculator() {
  const [facilities, setFacilities] = useState(8);
  const [residents, setResidents] = useState(90);
  const [minutes, setMinutes] = useState(12);
  const [hourly, setHourly] = useState(75);
  const [reduction, setReduction] = useState(50);

  const r = useMemo(() => {
    const totalMrrs = facilities * residents;
    const currentHours = (totalMrrs * minutes) / 60;
    const hoursSaved = currentHours * (reduction / 100);
    const dollarsSaved = hoursSaved * hourly;
    return {
      totalMrrs,
      currentHours,
      hoursSaved,
      dollarsSaved,
      hoursSavedYear: hoursSaved * 12,
      dollarsSavedYear: dollarsSaved * 12,
      fte: hoursSaved / 160,
    };
  }, [facilities, residents, minutes, hourly, reduction]);

  return (
    <>
      <Seo
        title="MRR ROI Calculator | WeConsult Rx"
        description="Estimate the hours and dollars a consultant pharmacist saves each month by cutting medication regimen review time. Free, runs in your browser."
        path="/roi-calculator"
        jsonLd={faqPageJsonLd(FAQS)}
      />

      <Section className="pb-8">
        <Container>
          <div className="max-w-3xl">
            <Eyebrow>ROI calculator</Eyebrow>
            <Heading level={1} className="mt-3">
              How much time is your MRR workflow really costing you?
            </Heading>
            <Lede className="mt-6">
              Estimate the hours and dollars freed each month by running the monthly medication regimen review
              in about half the time. Adjust the inputs to match your practice.
            </Lede>
          </div>
        </Container>
      </Section>

      <Section tone="soft" className="pt-0">
        <Container>
          <div className="grid gap-8 lg:grid-cols-[1fr_1fr] lg:items-start">
            {/* Inputs */}
            <div className="rounded-2xl border border-border bg-surface p-6 sm:p-8">
              <Heading level={3}>Your practice</Heading>
              <div className="mt-6 grid gap-5 sm:grid-cols-2">
                <Field label="Facilities covered" value={facilities} min={1} max={500} onChange={setFacilities} />
                <Field label="Avg residents / facility" value={residents} min={1} max={500} onChange={setResidents} />
                <Field label="Minutes per MRR today" suffix="min" value={minutes} min={1} max={120} onChange={setMinutes} />
                <Field label="Pharmacist cost" suffix="$/hr" value={hourly} min={1} max={500} onChange={setHourly} />
              </div>
              <div className="mt-6">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-ink-900">Time reduction</span>
                  <span className="font-mono text-sm font-bold text-forest-700">{reduction}%</span>
                </div>
                <input
                  type="range"
                  min={30}
                  max={60}
                  step={5}
                  value={reduction}
                  onChange={(e) => setReduction(Number(e.target.value))}
                  className="mt-3 w-full accent-forest-700"
                  aria-label="Time reduction percentage"
                />
                <p className="mt-1 text-xs text-ink-500">Conservative default is 50%. Slide to model your own assumption.</p>
              </div>
            </div>

            {/* Results */}
            <div className="rounded-2xl bg-forest-gradient p-6 text-white sm:p-8">
              <p className="text-sm font-semibold uppercase tracking-tagline text-white/80">Estimated savings</p>
              <div className="mt-6 grid grid-cols-2 gap-6">
                <div>
                  <p className="font-mono text-4xl font-extrabold tabular-nums">{fmtInt.format(Math.round(r.hoursSaved))}</p>
                  <p className="mt-1 text-sm text-white/80">hours saved / month</p>
                </div>
                <div>
                  <p className="font-mono text-4xl font-extrabold tabular-nums">{fmtUsd.format(r.dollarsSaved)}</p>
                  <p className="mt-1 text-sm text-white/80">saved / month</p>
                </div>
              </div>
              <div className="mt-6 border-t border-white/20 pt-6 text-sm text-white/90">
                <div className="flex justify-between py-1">
                  <span>Reviews / month</span>
                  <span className="font-mono tabular-nums">{fmtInt.format(r.totalMrrs)}</span>
                </div>
                <div className="flex justify-between py-1">
                  <span>Hours saved / year</span>
                  <span className="font-mono tabular-nums">{fmtInt.format(Math.round(r.hoursSavedYear))}</span>
                </div>
                <div className="flex justify-between py-1">
                  <span>Saved / year</span>
                  <span className="font-mono tabular-nums">{fmtUsd.format(r.dollarsSavedYear)}</span>
                </div>
                <div className="flex justify-between py-1">
                  <span>≈ added FTE capacity</span>
                  <span className="font-mono tabular-nums">{r.fte.toFixed(1)}</span>
                </div>
              </div>
              <div className="mt-8">
                <LinkButton to="/demo" variant="secondary">
                  See it on your own caseload
                </LinkButton>
              </div>
            </div>
          </div>
          <p className="mt-6 max-w-3xl text-xs text-ink-500">
            Estimate only, based on the time reduction you selected. Actual results vary by facility mix, chart
            complexity, and workflow. No inputs are submitted or stored — the math runs in your browser.
          </p>
        </Container>
      </Section>

      <FaqSection items={FAQS} />
    </>
  );
}
